import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { toErrorMessage } from '../../../helpers/http-error';
import { AuthService } from '../../../services/auth.service';
import { TPipe } from '../../../pipe/t.pipe';

@Component({
  selector: 'app-signin-form',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TPipe],
  templateUrl: './signin-form.component.html',
})
export class SigninFormComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly showPassword = signal(false);
  readonly submitting = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly form = this.fb.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });

  togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  onSignIn(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.errorMessage.set(null);

    this.auth.login(this.form.getRawValue()).subscribe({
      next: () => {
        // Reset the loader before navigating — if a guard bounces us back to
        // /signin (e.g. user lacks the required role), the component instance
        // may be reused and the spinner would otherwise hang.
        this.submitting.set(false);
        const returnUrl =
          this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
        void this.router.navigateByUrl(returnUrl);
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        // 401 / 400 → bad credentials. Other statuses fall through to the
        // generic helper. We override because the interceptor purposefully
        // does NOT trigger refresh-then-retry on /login itself.
        this.errorMessage.set(
          toErrorMessage(err, 'errors.signinFailed', {
            0: 'errors.authServerUnreachable',
            400: 'errors.badCredentials',
            401: 'errors.badCredentials',
          }),
        );
      },
    });
  }
}
